/**
 * SmartEssaySyncButton - AI-powered essay requirements fetching and syncing
 * Integrates AI fetch, diff preview, and smart sync with draft preservation
 */

import React, { useState } from 'react';
import { Sparkles, RefreshCw, Loader2, CheckCircle, AlertTriangle, Eye } from 'lucide-react';
import { toast } from 'react-hot-toast';
import EssaySlotSyncService from '../../services/EssaySlotSyncService';
import EssayDeletionConfirmModal from './EssayDeletionConfirmModal';

const SmartEssaySyncButton = ({
  collegeId,
  userId,
  collegeName,
  collegeWebsite,
  applicationYear = '2025-2026',
  onFetchComplete, // Callback after AI fetch
  onSyncComplete,  // Callback after sync
  variant = 'default', // 'default' | 'compact'
  className = ''
}) => {
  const [fetching, setFetching] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [diffData, setDiffData] = useState(null);
  const [fetchedData, setFetchedData] = useState(null);

  /**
   * Fetch essay requirements from AI
   */
  const handleFetchFromAI = async () => {
    if (!collegeName) {
      toast.error('College name is required');
      return;
    }

    setFetching(true);
    const toastId = toast.loading(`Fetching essay requirements for ${collegeName}...`);

    try {
      const response = await fetch('/api/anthropic/fetch-essay-requirements', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          collegeName,
          collegeWebsite,
          applicationYear
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch essay requirements');
      }

      const { essayRequirements, metadata } = data;

      console.log('[SmartEssaySyncButton] Fetched requirements:', essayRequirements);

      setFetchedData(essayRequirements);

      toast.success(
        `Found ${essayRequirements.essays.length} essay${essayRequirements.essays.length !== 1 ? 's' : ''} (${essayRequirements.confidence_score}% confidence)`,
        {
          id: toastId,
          duration: 4000
        }
      );

      // Call callback with fetched data
      if (onFetchComplete) {
        onFetchComplete(essayRequirements);
      }

    } catch (error) {
      console.error('[SmartEssaySyncButton] Fetch error:', error);
      toast.error(`Failed to fetch: ${error.message}`, {
        id: toastId,
        duration: 5000
      });
    } finally {
      setFetching(false);
    }
  };

  /**
   * Sync essay slots with requirements (with confirmation if needed)
   */
  const handleSync = async () => {
    if (!collegeId || !userId) {
      toast.error('Missing college ID or user ID');
      return;
    }

    setSyncing(true);
    const toastId = toast.loading('Checking for changes...');

    try {
      // First, get the diff preview
      const result = await EssaySlotSyncService.syncWithConfirmation(collegeId, userId, false);

      if (result.needsConfirmation) {
        // Show confirmation modal
        setDiffData(result.diff);
        setShowConfirmModal(true);
        toast.dismiss(toastId);
      } else {
        // No confirmation needed, sync is complete
        const { results } = result;
        displaySyncResults(results, toastId);

        if (onSyncComplete) {
          onSyncComplete(results);
        }
      }

    } catch (error) {
      console.error('[SmartEssaySyncButton] Sync error:', error);
      toast.error(`Failed to sync: ${error.message}`, {
        id: toastId,
        duration: 5000
      });
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Confirm and apply changes after user reviews
   */
  const handleConfirmSync = async () => {
    setSyncing(true);
    const toastId = toast.loading('Applying changes...');

    try {
      // Apply changes with confirmation
      const result = await EssaySlotSyncService.syncWithConfirmation(collegeId, userId, true);

      const { results } = result;
      displaySyncResults(results, toastId);

      setShowConfirmModal(false);
      setDiffData(null);

      if (onSyncComplete) {
        onSyncComplete(results);
      }

    } catch (error) {
      console.error('[SmartEssaySyncButton] Confirm sync error:', error);
      toast.error(`Failed to apply changes: ${error.message}`, {
        id: toastId,
        duration: 5000
      });
    } finally {
      setSyncing(false);
    }
  };

  /**
   * Display sync results to user
   */
  const displaySyncResults = (results, toastId) => {
    const messages = [];

    if (results.created > 0) {
      messages.push(`${results.created} essay${results.created !== 1 ? 's' : ''} added`);
    }
    if (results.updated > 0) {
      messages.push(`${results.updated} updated`);
    }
    if (results.deleted > 0) {
      messages.push(`${results.deleted} deleted`);
    }
    if (results.preserved > 0) {
      messages.push(`${results.preserved} draft${results.preserved !== 1 ? 's' : ''} preserved`);
    }
    if (messages.length === 0 && results.unchanged >= 0) {
      messages.push('Already in sync');
    }

    const message = messages.join(', ');

    if (results.errors.length > 0) {
      toast.error(`${message}, but ${results.errors.length} error${results.errors.length !== 1 ? 's' : ''} occurred`, {
        id: toastId,
        duration: 6000
      });
    } else {
      toast.success(message, {
        id: toastId,
        duration: 4000
      });
    }
  };

  // Compact variant
  if (variant === 'compact') {
    return (
      <>
        <div className={`flex items-center gap-2 ${className}`}>
          <button
            onClick={handleFetchFromAI}
            disabled={fetching || syncing}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-all ${
              fetching || syncing
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-sm hover:shadow'
            }`}
            title="Fetch essay requirements using AI"
          >
            {fetching ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            <span>{fetching ? 'Fetching...' : 'AI Fetch'}</span>
          </button>

          <button
            onClick={handleSync}
            disabled={fetching || syncing}
            className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg transition-all ${
              fetching || syncing
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-white border border-gray-300 hover:border-gray-400 text-gray-700 hover:text-gray-900'
            }`}
            title="Sync essay slots with requirements"
          >
            {syncing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <RefreshCw className="w-3.5 h-3.5" />
            )}
            <span>{syncing ? 'Syncing...' : 'Sync'}</span>
          </button>
        </div>

        <EssayDeletionConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          diff={diffData}
          collegeName={collegeName}
          onConfirm={handleConfirmSync}
          loading={syncing}
        />
      </>
    );
  }

  // Default variant (full buttons)
  return (
    <>
      <div className={`space-y-3 ${className}`}>
        <div className="flex items-center gap-3">
          {/* AI Fetch Button */}
          <button
            onClick={handleFetchFromAI}
            disabled={fetching || syncing}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              fetching || syncing
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-sm hover:shadow'
            }`}
          >
            {fetching ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Fetching from AI...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Fetch Essays from AI
              </>
            )}
          </button>

          {/* Sync Button */}
          <button
            onClick={handleSync}
            disabled={fetching || syncing}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
              fetching || syncing
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm hover:shadow'
            }`}
          >
            {syncing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Sync Essay Slots
              </>
            )}
          </button>
        </div>

        {/* Info text */}
        <div className="text-xs text-gray-600 flex items-start gap-1">
          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-amber-500" />
          <div>
            <strong>AI Fetch:</strong> Retrieves essay requirements from college website.
            <strong className="ml-2">Sync:</strong> Updates essay slots and creates timeline tasks.
          </div>
        </div>

        {/* Fetched data preview */}
        {fetchedData && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-green-800">
              <CheckCircle className="w-4 h-4" />
              <span>
                Found {fetchedData.essays.length} essay{fetchedData.essays.length !== 1 ? 's' : ''}
                {' '}({fetchedData.confidence_score}% confidence)
              </span>
            </div>
            <div className="text-xs text-green-700 mt-1">
              Click "Sync Essay Slots" to apply changes
            </div>
          </div>
        )}
      </div>

      <EssayDeletionConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        diff={diffData}
        collegeName={collegeName}
        onConfirm={handleConfirmSync}
        loading={syncing}
      />
    </>
  );
};

export default SmartEssaySyncButton;
